<template>
    <Card :pageTitle="pageTitle" full-width>
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_EMAIL_TEMPLATE">
            <div class="versions-page">
                <header class="versions-header">
                    <router-link to="/settings/email-templates" class="back-link">
                        <span class="material-icons">arrow_back</span>
                        Templates
                    </router-link>
                    <div class="title-line">
                        <h1>Version history</h1>
                        <span class="published-pill" v-if="template && template.isPublished">
                            Published v{{ template.publishedVersion }}
                        </span>
                    </div>
                    <p>Every publish is an immutable snapshot. Restoring a version copies it back into the draft for review — it does not change what is live until you publish again.</p>
                </header>

                <div v-if="loading" class="versions-state">
                    <span class="spinner" aria-hidden="true"></span>
                    <p>Loading version history…</p>
                </div>

                <div v-else-if="loadError" class="versions-state error-state" role="alert">
                    <span class="material-icons">error_outline</span>
                    <p>{{ loadError }}</p>
                    <button type="button" class="secondary-button" @click="loadVersions">Try again</button>
                </div>

                <div v-else-if="versions.length === 0" class="versions-state">
                    <span class="material-icons">history</span>
                    <p>No published versions yet. Publish this template to create the first snapshot.</p>
                </div>

                <div v-else class="versions-layout">
                    <nav class="version-list" aria-label="Versions">
                        <button
                            v-for="version in versions"
                            :key="version.version"
                            type="button"
                            :class="['version-entry', { active: selectedVersion === version.version }]"
                            @click="selectVersion(version.version)"
                        >
                            <span class="version-number">v{{ version.version }}</span>
                            <span class="version-subject">{{ version.subject }}</span>
                            <span class="version-meta">
                                {{ formatDate(version.createdAt) }}{{ version.createdBy ? ` · ${version.createdBy}` : "" }}
                            </span>
                        </button>
                    </nav>

                    <section class="version-preview">
                        <div v-if="!preview" class="preview-empty">Select a version to preview its published HTML.</div>
                        <template v-else>
                            <div class="preview-actions">
                                <span class="preview-label">v{{ preview.version }} — {{ preview.subject }}</span>
                                <button
                                    v-if="canEdit"
                                    type="button"
                                    class="restore-button"
                                    :disabled="restoring"
                                    @click="restoreVersion"
                                >
                                    <span class="material-icons">restore</span>
                                    {{ restoring ? "Restoring…" : "Restore to draft" }}
                                </button>
                            </div>
                            <iframe class="preview-frame" :srcdoc="preview.html" sandbox title="Email version preview"></iframe>
                        </template>
                    </section>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import Card from "../../components/UI/Card.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import EmailTemplateFetchApi from "../../utils/fetch/email-template-fetch-api"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import { confirmDialog, errMsg } from "../../utils/common"

export default {
    components: { Card, AllowPermission },
    data() {
        return {
            PERMISSION_CODES,
            templateKey: "",
            template: null,
            versions: [],
            selectedVersion: null,
            preview: null,
            loading: true,
            loadError: "",
            restoring: false,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_EMAIL_TEMPLATE,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        pageTitle() {
            return this.template ? `${this.template.label} — versions` : "Version history"
        },
    },
    mounted() {
        this.templateKey = `${this.$route.params.templateKey || ""}`
        this.loadVersions()
    },
    methods: {
        async loadVersions() {
            this.loading = true
            this.loadError = ""
            try {
                this.template = await EmailTemplateFetchApi.get(this.templateKey)
                this.versions = await EmailTemplateFetchApi.listVersions(this.templateKey)
                if (this.versions.length) {
                    this.selectedVersion = this.versions[0].version
                    this.preview = null
                    await this.selectVersion(this.selectedVersion)
                }
            } catch (err) {
                this.loadError = errMsg(err, "Version history could not be loaded.")
            }
            this.loading = false
        },
        async selectVersion(version) {
            this.selectedVersion = version
            try {
                this.preview = await EmailTemplateFetchApi.getVersion(this.templateKey, version)
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load this version."))
            }
        },
        async restoreVersion() {
            if (this.restoring || !this.preview) return
            const confirmed = await confirmDialog({
                title: `Restore v${this.preview.version} to the draft?`,
                text: "The draft is replaced with this version's content. Published delivery is unchanged until you publish again.",
                confirmButtonText: "Restore to draft",
            })
            if (!confirmed) return
            this.restoring = true
            try {
                await EmailTemplateFetchApi.rollback(this.templateKey, {
                    version: this.preview.version,
                    expectedDraftRevision: this.template?.draftRevision,
                })
                this.$toast.success(`v${this.preview.version} restored to the draft`)
                this.$router.push(`/settings/email-templates/${this.templateKey}`)
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not restore this version"))
            } finally {
                this.restoring = false
            }
        },
        formatDate(value) {
            const date = new Date(value)
            if (Number.isNaN(date.getTime())) return "recently"
            return new Intl.DateTimeFormat("en-CA", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
            }).format(date)
        },
    },
}
</script>

<style lang="scss" scoped>
.versions-page {
    color: #173b35;
}

.versions-header {
    padding: 2px 0 18px;
    border-bottom: 1px solid #dce5e1;
}

.back-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 9px;
    color: #5f746d;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
}

.title-line {
    display: flex;
    align-items: center;
    gap: 10px;
}

.versions-header h1 {
    margin: 0;
    font-size: 25px;
    font-weight: 780;
}

.versions-header p {
    max-width: 720px;
    margin: 7px 0 0;
    color: #65766f;
    font-size: 13px;
    line-height: 1.5;
}

.published-pill {
    padding: 4px 9px;
    border-radius: 999px;
    background: #e2f1eb;
    color: #236047;
    font-size: 11px;
    font-weight: 800;
}

.versions-state {
    display: grid;
    min-height: 240px;
    place-items: center;
    align-content: center;
    gap: 12px;
    color: #65766f;
    font-size: 13px;
}

.versions-state.error-state {
    color: #a34b3e;
}

.versions-state > .material-icons {
    color: #a8b6b1;
    font-size: 30px;
}

.versions-layout {
    display: grid;
    grid-template-columns: 300px minmax(0, 1fr);
    gap: 18px;
    margin-top: 18px;
}

.version-list {
    display: grid;
    align-content: start;
    gap: 6px;
    max-height: 640px;
    overflow-y: auto;
    padding-right: 6px;
}

.version-entry {
    display: grid;
    gap: 3px;
    padding: 10px 12px;
    border: 1px solid #dde6e2;
    border-radius: 8px;
    background: #fbfdfc;
    color: #173b35;
    text-align: left;
    cursor: pointer;
}

.version-entry.active {
    border-color: #123b34;
    box-shadow: 0 0 0 2px rgba(18, 59, 52, 0.14);
}

.version-number {
    font-size: 13px;
    font-weight: 800;
    color: #1d5a4d;
}

.version-subject {
    overflow: hidden;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.version-meta {
    color: #7a8984;
    font-size: 10px;
}

.version-preview {
    display: grid;
    min-height: 560px;
    align-content: start;
    gap: 10px;
}

.preview-empty {
    display: grid;
    min-height: 400px;
    place-items: center;
    border: 1px dashed #d4dfdb;
    border-radius: 8px;
    color: #7a8984;
    font-size: 13px;
}

.preview-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.preview-label {
    color: #536961;
    font-size: 12px;
    font-weight: 800;
}

.restore-button {
    display: inline-flex;
    min-height: 38px;
    align-items: center;
    gap: 6px;
    padding: 0 13px;
    border: 1px solid #123b34;
    border-radius: 7px;
    background: #123b34;
    color: #fff;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
}

.restore-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.preview-frame {
    width: 100%;
    min-height: 520px;
    border: 1px solid #d2ddd9;
    border-radius: 8px;
    background: #fff;
}

.secondary-button {
    padding: 8px 14px;
    border: 1px solid #c9d6d1;
    border-radius: 7px;
    background: #fff;
    color: #173b35;
    font-size: 13px;
    font-weight: 750;
    cursor: pointer;
}

.spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #d5e1dc;
    border-top-color: #1e5a4c;
    border-radius: 50%;
    animation: spin 900ms linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

@media (max-width: 860px) {
    .versions-layout {
        grid-template-columns: 1fr;
    }
}
</style>
